// "Označi plaćeno" with no signal: the mark waits in IndexedDB until it can be sent.
// No DOM use, so the service worker can flush it too (Background Sync).
import { post, ApiError } from './api.js';

const DB = 'ws-outbox';
const STORE = 'paid';

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'bookingId' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// One transaction on the store; resolves with the request's result once it commits.
async function tx(mode, fn) {
  const db = await open();
  try {
    return await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, mode);
      const req = fn(t.objectStore(STORE));
      t.oncomplete = () => resolve(req?.result);
      t.onerror = t.onabort = () => reject(t.error);
    });
  } finally {
    db.close();
  }
}

const changed = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('outbox-change'));
};

export async function queuePaid(bookingId) {
  await tx('readwrite', (s) => s.put({ bookingId }));
  changed();
  navigator.serviceWorker?.ready.then((r) => r.sync?.register('outbox')).catch(() => {});
}

export async function pendingPaid() {
  const keys = await tx('readonly', (s) => s.getAllKeys());
  return new Set(keys);
}

export async function flushOutbox(fetchFn = fetch) {
  const ids = [...(await pendingPaid())];
  let sent = 0;
  for (const id of ids) {
    let res;
    try {
      res = await fetchFn(`/api/bookings/${id}/paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paid: true }),
        credentials: 'same-origin',
      });
    } catch {
      break; // no signal: the rest waits for the next try
    }
    // 5xx, 401 (signed out) and 429 (too many tries) may pass later: stop and keep the rest
    if (res.status >= 500 || res.status === 401 || res.status === 429) break;
    await tx('readwrite', (s) => s.delete(id)); // 2xx sent; 400/403/404/409 refused, and would be refused again
    if (res.ok) sent++;
  }
  if (ids.length) changed();
  return sent;
}

export async function clearOutbox() {
  await tx('readwrite', (s) => s.clear());
  changed();
}

export async function markPaid(bookingId) {
  try {
    await post(`/bookings/${bookingId}/paid`, { paid: true });
    return 'sent';
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 0) throw e;
    await queuePaid(bookingId);
    return 'queued';
  }
}
